import React from 'react';

import { JaaSMeeting } from '@jitsi/react-sdk';
import { GripVertical, Maximize2, Minimize2, Power } from 'lucide-react';
import Draggable from 'react-draggable';

import { Spin } from 'components/Spin';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';

import type { MeetingRoomData } from './index';

type IJitsiAuth = {
  app_id: string;
  token: string;
};

export interface MeetingPiPOverlayProps {
  isActive: boolean;
  isPiP: boolean;
  isFullscreen: boolean;
  isAttached: boolean;
  room: MeetingRoomData | null;
  auth: IJitsiAuth | null;
  roomName: string | null;
  attachedRect: { top: number; left: number; width: number; height: number } | null;
  dragPos: { x: number; y: number };
  onDragPosChange: (pos: { x: number; y: number }) => void;
  onLeaveMeeting: () => void;
  onToggleFullscreen: () => void;
  externalApiRef: React.MutableRefObject<any>;
  onReadyRef: React.MutableRefObject<(() => void) | null>;
}

/**
 * Compute wrapper classes based on state
 */
const getWrapperClasses = (isFullscreen: boolean, isAttached: boolean): string => {
  const base = 'fixed z-[1200] overflow-visible touch-manipulation';

  if (isFullscreen) {
    return cn(
      base,
      'inset-0 w-screen h-screen rounded-none bg-black z-[5000]',
      '[&:hover_.pip-controls]:opacity-100 [&:hover_.pip-controls]:pointer-events-auto'
    );
  }

  if (!isAttached) {
    // PiP mode
    return cn(
      base,
      'shadow-xl bg-black rounded-lg z-[2500] w-[min(360px,90vw)] max-w-[90vw] aspect-video',
      '[&:hover_.pip-controls]:opacity-100 [&:hover_.pip-controls]:pointer-events-auto'
    );
  }

  // Attached mode (default)
  return cn(base, 'shadow-none rounded-none bg-transparent');
};

/**
 * MeetingPiPOverlay - Renders the Picture-in-Picture overlay for active meetings
 *
 * This component handles the visual rendering of the Jitsi meeting in various modes:
 * - PiP (Picture-in-Picture): Draggable floating window
 * - Fullscreen: Full viewport coverage
 * - Attached: Positioned over an in-page placeholder element
 */
export const MeetingPiPOverlay: React.FC<MeetingPiPOverlayProps> = ({
  isActive,
  isPiP,
  isFullscreen,
  isAttached,
  room,
  auth,
  roomName,
  attachedRect,
  dragPos,
  onDragPosChange,
  onLeaveMeeting,
  onToggleFullscreen,
  externalApiRef,
  onReadyRef,
}) => {
  // Don't render if not active or missing required data
  if (!isActive || !room || !auth) {
    return null;
  }

  const wrapperClasses = getWrapperClasses(isFullscreen, isAttached);

  // Compute inline styles based on mode
  const getWrapperStyle = (): React.CSSProperties | undefined => {
    if (isFullscreen) {
      return undefined;
    }

    if (isAttached && attachedRect) {
      return {
        top: attachedRect.top,
        left: attachedRect.left,
        width: attachedRect.width,
        height: attachedRect.height,
      };
    }

    // PiP mode - bottom right corner
    return { bottom: 16, right: 16 };
  };

  const handleDrag = (_e: any, data: { x: number; y: number }) => {
    onDragPosChange({ x: data.x, y: data.y });
  };

  const handleApiReady = (externalApi: any) => {
    externalApiRef.current = externalApi;
    try {
      const subject =
        room?.room_data?.title ||
        `Sala ${String(room?.room_data?.internal_id ?? room?.room_data?.id ?? '')}`;
      externalApi.executeCommand('subject', subject);
    } catch {
      // Ignore errors when setting subject
    }
    onReadyRef.current?.();
  };

  return (
    <Draggable
      handle=".pip-drag-handle"
      cancel="button, [role='button']"
      bounds="body"
      disabled={isFullscreen || isAttached}
      position={isFullscreen || isAttached ? { x: 0, y: 0 } : dragPos}
      onDrag={handleDrag}
    >
      <div
        className={wrapperClasses}
        data-fullscreen={isFullscreen ? 'true' : 'false'}
        data-pip={!isFullscreen && !isAttached ? 'true' : 'false'}
        style={getWrapperStyle()}
      >
        {/* PiP Controls - visible on hover */}
        {isActive && isPiP && (
          <PiPControls
            isFullscreen={isFullscreen}
            onLeaveMeeting={onLeaveMeeting}
            onToggleFullscreen={onToggleFullscreen}
          />
        )}

        {/* Jitsi Meeting Container */}
        <div className="absolute top-7 left-0 right-0 bottom-0 [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0">
          <JaaSMeeting
            appId={auth.app_id}
            roomName={roomName ?? room?.room_data.title ?? ''}
            jwt={auth.token}
            configOverwrite={{
              disableLocalVideoFlip: true,
              backgroundAlpha: 0,
              defaultLanguage: 'pt-BR',
              toolbarConfig: { timeout: 2000 },
              subject:
                room?.room_data?.title ||
                `Sala ${String(room?.room_data?.internal_id ?? room?.room_data?.id ?? '')}`,
            }}
            interfaceConfigOverwrite={{
              VIDEO_LAYOUT_FIT: 'nocrop',
              MOBILE_APP_PROMO: false,
              TILE_VIEW_MAX_COLUMNS: 4,
            }}
            spinner={Spin}
            onApiReady={handleApiReady}
            onReadyToClose={onLeaveMeeting}
          />
        </div>
      </div>
    </Draggable>
  );
};

/**
 * PiPControls - Control bar for the PiP overlay
 */
interface PiPControlsProps {
  isFullscreen: boolean;
  onLeaveMeeting: () => void;
  onToggleFullscreen: () => void;
}

const PiPControls: React.FC<PiPControlsProps> = ({
  isFullscreen,
  onLeaveMeeting,
  onToggleFullscreen,
}) => {
  return (
    <div
      className={cn(
        'pip-drag-handle pip-controls',
        'absolute top-0 left-0 right-0 h-6 flex items-center justify-between gap-1.5 p-1 z-[2600]',
        'bg-gradient-to-b from-black/60 to-transparent rounded-t-lg',
        'opacity-0 pointer-events-none transition-opacity duration-[120ms] ease-in-out',
        'touch-action:opacity-100 touch-action:pointer-events-auto',
        '[&_button]:text-sm [&_button]:px-1.5 [&_button]:py-0.5 [&_button]:h-8'
      )}
    >
      <GripVertical className="w-5 h-5 text-primary cursor-grab" />
      <div className="flex items-center gap-2">
        <TooltipProvider>
          {/* Leave meeting button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="large"
                variant="ghost"
                icon={<Power className="h-4 w-4" />}
                onClick={onLeaveMeeting}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>Sair</p>
            </TooltipContent>
          </Tooltip>

          {/* Fullscreen toggle button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="large"
                variant="ghost"
                icon={
                  isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )
                }
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{isFullscreen ? 'Minimizar' : 'Maximizar'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default MeetingPiPOverlay;
